/**
 * LRU warm-start caches for bar-chart auto label mode and angled margins.
 * Keys are scoped by `cacheId` and category content so remounts skip layout jumps.
 */

import type { EffectiveCategoryLabelMode } from '../shared/chart-tokens';

import { LruMap } from './lru-map';

/** Caps remount warm-start entries for long AI chat sessions (module-level cache). */
export const RESOLVED_AXIS_MARGIN_CACHE_MAX_ENTRIES = 100;
export const RESOLVED_AUTO_LABEL_MODE_CACHE_MAX_ENTRIES = 100;

export interface ResolvedAxisMargins {
  left: number;
  bottom: number;
}

const resolvedAxisMarginCache = new LruMap<string, ResolvedAxisMargins>(
  RESOLVED_AXIS_MARGIN_CACHE_MAX_ENTRIES
);
const resolvedAutoLabelModeCache = new LruMap<
  string,
  EffectiveCategoryLabelMode
>(RESOLVED_AUTO_LABEL_MODE_CACHE_MAX_ENTRIES);

export const serializeChartCacheKey = (parts: readonly unknown[]): string =>
  JSON.stringify(parts);

export const buildMarginCacheKey = (
  cacheId: string,
  effectiveMode: EffectiveCategoryLabelMode,
  categoriesKey: string
): string =>
  serializeChartCacheKey(['margin', cacheId, effectiveMode, categoriesKey]);

export const buildAutoModeCacheKey = (
  cacheId: string,
  categoriesKey: string
): string => serializeChartCacheKey(['mode', cacheId, 'auto', categoriesKey]);

export const getResolvedAxisMargin = (
  key: string
): ResolvedAxisMargins | undefined => resolvedAxisMarginCache.get(key);

export const setResolvedAxisMargin = (
  key: string,
  value: ResolvedAxisMargins
): void => {
  resolvedAxisMarginCache.set(key, value);
};

export const getResolvedAutoLabelMode = (
  key: string
): EffectiveCategoryLabelMode | undefined =>
  resolvedAutoLabelModeCache.get(key);

export const setResolvedAutoLabelMode = (
  key: string,
  value: EffectiveCategoryLabelMode
): void => {
  resolvedAutoLabelModeCache.set(key, value);
};

/** Resets module-level cache between tests; not used in production. */
export const clearResolvedAxisMarginCacheForTests = (): void => {
  resolvedAxisMarginCache.clear();
};

/** Resets auto-mode cache between tests; not used in production. */
export const clearResolvedAutoLabelModeCacheForTests = (): void => {
  resolvedAutoLabelModeCache.clear();
};
